import { useState } from 'react';
import { Box, Stack, Group, Badge, TextInput, Select, Table, ActionIcon } from '@mantine/core';
import {
  IconSearch,
  IconDownload,
  IconFileSpreadsheet,
  IconFileTypePdf,
  IconFileText,
  IconCode,
  IconLoader,
  IconX,
} from '@tabler/icons-react';
import { Card, Text, Title, Button } from '../../../../components/ui';
import { mockExports, ExportRecord } from '../mockData/analytics';

const formatDate = (date: Date) => {
  return new Intl.DateTimeFormat('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  }).format(date);
};

const formatFileSize = (bytes: number): string => {
  if (bytes === 0) return '-';
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
};

const getFormatIcon = (format: ExportRecord['format']) => {
  switch (format) {
    case 'xlsx': return IconFileSpreadsheet;
    case 'csv': return IconFileText;
    case 'pdf': return IconFileTypePdf;
    case 'json': return IconCode;
    default: return IconFileText;
  }
};

const getFormatColor = (format: ExportRecord['format']) => {
  switch (format) {
    case 'xlsx': return 'green';
    case 'csv': return 'blue';
    case 'pdf': return 'red';
    case 'json': return 'violet';
    default: return 'gray';
  }
};

const getStatusColor = (status: ExportRecord['status']) => {
  switch (status) {
    case 'completed': return 'green';
    case 'processing': return 'blue';
    case 'failed': return 'red';
    default: return 'gray';
  }
};

export default function ExportsPage() {
  const [search, setSearch] = useState('');
  const [formatFilter, setFormatFilter] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState<string | null>(null);

  const formats = ['xlsx', 'csv', 'pdf', 'json'];
  const statuses = ['completed', 'processing', 'failed'];

  const filteredExports = mockExports.filter(exp => {
    const matchesSearch = exp.reportName.toLowerCase().includes(search.toLowerCase());
    const matchesFormat = !formatFilter || exp.format === formatFilter;
    const matchesStatus = !statusFilter || exp.status === statusFilter;
    return matchesSearch && matchesFormat && matchesStatus;
  });

  // Sort by date descending
  const sortedExports = [...filteredExports].sort((a, b) => b.createdAt.getTime() - a.createdAt.getTime());

  const completedCount = mockExports.filter(e => e.status === 'completed').length;
  const processingCount = mockExports.filter(e => e.status === 'processing').length;

  return (
    <Stack gap="lg">
      {/* Header */}
      <Group justify="space-between">
        <Box>
          <Title order={2}>Export History</Title>
          <Text c="dimmed" size="sm">{filteredExports.length} exports</Text>
        </Box>
        <Button leftSection={<IconDownload size={16} />}>New Export</Button>
      </Group>

      {/* Summary Cards */}
      <Group>
        <Card style={{ flex: 1 }}>
          <Text size="xs" c="dimmed">Total Exports</Text>
          <Text size="xl" fw={700}>{mockExports.length}</Text>
        </Card>
        <Card style={{ flex: 1 }}>
          <Text size="xs" c="dimmed">Completed</Text>
          <Text size="xl" fw={700} c="green">{completedCount}</Text>
        </Card>
        <Card style={{ flex: 1 }}>
          <Text size="xs" c="dimmed">Processing</Text>
          <Text size="xl" fw={700} c="blue">{processingCount}</Text>
        </Card>
      </Group>

      {/* Filters */}
      <Card>
        <Group>
          <TextInput
            placeholder="Search exports..."
            leftSection={<IconSearch size={16} />}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            style={{ flex: 1 }}
          />
          <Select
            placeholder="Format"
            data={formats.map(f => ({ value: f, label: f.toUpperCase() }))}
            value={formatFilter}
            onChange={setFormatFilter}
            clearable
            w={120}
          />
          <Select
            placeholder="Status"
            data={statuses.map(s => ({ value: s, label: s.charAt(0).toUpperCase() + s.slice(1) }))}
            value={statusFilter}
            onChange={setStatusFilter}
            clearable
            w={150}
          />
        </Group>
      </Card>

      {/* Exports Table */}
      <Card>
        <Table>
          <Table.Thead>
            <Table.Tr>
              <Table.Th>Report</Table.Th>
              <Table.Th>Format</Table.Th>
              <Table.Th>Size</Table.Th>
              <Table.Th>Created</Table.Th>
              <Table.Th>Created By</Table.Th>
              <Table.Th>Status</Table.Th>
              <Table.Th w={60}></Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {sortedExports.map(exp => {
              const FormatIcon = getFormatIcon(exp.format);
              const formatColor = getFormatColor(exp.format);
              return (
                <Table.Tr key={exp.id}>
                  <Table.Td>
                    <Group gap="sm">
                      <Box
                        style={{
                          width: 32,
                          height: 32,
                          borderRadius: 6,
                          backgroundColor: `var(--mantine-color-${formatColor}-light)`,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                        }}
                      >
                        <FormatIcon size={16} color={`var(--mantine-color-${formatColor}-filled)`} />
                      </Box>
                      <Text size="sm">{exp.reportName}</Text>
                    </Group>
                  </Table.Td>
                  <Table.Td>
                    <Badge size="sm" variant="light" color={formatColor}>
                      {exp.format.toUpperCase()}
                    </Badge>
                  </Table.Td>
                  <Table.Td>
                    <Text size="sm" c="dimmed">{formatFileSize(exp.size)}</Text>
                  </Table.Td>
                  <Table.Td>
                    <Text size="sm" c="dimmed">{formatDate(exp.createdAt)}</Text>
                  </Table.Td>
                  <Table.Td>
                    <Text size="sm">{exp.createdBy}</Text>
                  </Table.Td>
                  <Table.Td>
                    <Badge
                      size="sm"
                      color={getStatusColor(exp.status)}
                      leftSection={exp.status === 'processing' ? <IconLoader size={10} /> : null}
                    >
                      {exp.status}
                    </Badge>
                  </Table.Td>
                  <Table.Td>
                    {exp.status === 'completed' && (
                      <ActionIcon variant="light" size="sm" title="Download">
                        <IconDownload size={14} />
                      </ActionIcon>
                    )}
                    {exp.status === 'processing' && (
                      <ActionIcon variant="light" color="red" size="sm" title="Cancel">
                        <IconX size={14} />
                      </ActionIcon>
                    )}
                  </Table.Td>
                </Table.Tr>
              );
            })}
          </Table.Tbody>
        </Table>
      </Card>
    </Stack>
  );
}
